import { ColumnDef } from "@tanstack/react-table";
import { Stack } from "@/core/components/ui/stack";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/core/components/ui/tooltip";
import { Typography } from "@/core/components/ui/typography";
import { dayjs } from "@/core/utils/date";
import {
  formatDate,
  formatDateTimeWithZone,
  formatRelativeDate,
} from "@/core/utils/date";
import type { EnrichedSpiffApproval } from "@/spiff-approvals";

const PaymentDateCol = <
  T extends Pick<EnrichedSpiffApproval, "display_date">,
>(): ColumnDef<T> => ({
  id: "approval-date",
  header: "Approval Date",
  enablePinning: true,
  accessorKey: "display_date",
  enableSorting: true,
  sortingFn: (rowA, rowB) => {
    const a = dayjs(rowA.original.display_date);
    const b = dayjs(rowB.original.display_date);
    const aValid = a.isValid();
    const bValid = b.isValid();
    if (!aValid && !bValid) return 0;
    if (!aValid) return 1; // invalids sort last
    if (!bValid) return -1;
    return a.valueOf() - b.valueOf();
  },
  sortDescFirst: true,
  size: 140,

  cell: ({ row }) => {
    const date = row.original.display_date;

    const relativeDate = formatRelativeDate(date);
    const formattedDate = formatDate(date);
    const fullDateTime = formatDateTimeWithZone(date);

    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <div>
            <Stack direction="column" gap="none">
              <Typography variant="bodyW1">{relativeDate}</Typography>
              <Typography variant="captionW1" className="text-quaternary-text">
                {formattedDate}
              </Typography>
            </Stack>
          </div>
        </TooltipTrigger>
        <TooltipContent>{fullDateTime}</TooltipContent>
      </Tooltip>
    );
  },
});

export default PaymentDateCol;
